/**
 * Deep copy function for TypeScript.
 * @param T Generic type of target/copied value.
 * @param target Target value to be copied.
 * @see Source project, ts-deepcopy https://github.com/ykdr2017/ts-deepcopy
 * @see Code pen https://codepen.io/erikvullings/pen/ejyBYg
 * MIT License
 */
export const deepCopy = <T>(target: T): T => {
  if (target === null) {
    return target
  }
  if (target instanceof Date) {
    return new Date(target.getTime()) as any
  }
  if (target instanceof Array) {
    const cp = [] as any[]
    ;(target as any[]).forEach((v) => {
      cp.push(v)
    })
    return cp.map((n: any) => deepCopy<any>(n)) as any
  }
  if (typeof target === 'object' && target !== {} && Object.keys(target).length !== 0) {
    const cp = { ...(target as { [key: string]: any }) } as { [key: string]: any }
    Object.keys(cp).forEach((k) => {
      cp[k] = deepCopy<any>(cp[k])
    })
    return cp as T
  }
  return target
}

/**
 * Makes a deep copy of an object and sets field to the given value
 * @param name Name of the field to be set. If deep in the object, use "."
 * as a delimetter.
 * @param target Object to be modified (copied)
 * @param value Value of the field to be set
 */
export const deepCopyAndSet = <_, T>(name: string, target: any, value: string): T => {
  const path = name.split('.')
  const cp = deepCopy(target)
  let place = cp
  for (let i = 0; i < path.length; i++) {
    const name = path[i]
    if (i + 1 === path.length) {
      place[name] = value
    } else {
      if (place[name] == null) {
        place[name] = {}
      }
      place = place[name]
    }
  }
  return cp
}

/**
 * Makes a deep copy of an object and removes a field.
 * @param name Name of the field to be set. If deep in the object, use "."
 * as a delimetter.
 * @param target Object to be modified (copied)
 */
export const deepCopyAndRemove = <_, T>(name: string, target: any): T => {
  const path = name.split('.')
  const cp = deepCopy(target)
  let place = cp
  for (let i = 0; i < path.length; i++) {
    const name = path[i]
    if (i + 1 === path.length) {
      delete place[name]
    } else {
      if (place[name] == null) {
        place[name] = {}
      }
      place = place[name]
    }
  }
  return cp
}

/**
 * merge will copy non-null values from the source object
 * to the target object, recursively
 * @param source
 * @param target
 */
export const merge = (source: any, target: any): void => {
  if (source == null || target == null) return
  Object.keys(source).forEach((key) => {
    if (source[key] == null) return
    if (key in target && typeof source[key] == 'object') {
      merge(source[key], target[key])
    } else {
      target[key] = deepCopy(source[key])
    }
  })
}

/**
 * deepCopyAndMerge will make a deep copy of the target object
 * and merge all non-null values from the source object into the
 * target object (does not deep copy from source).
 * @param source
 * @param target
 */
export const deepCopyAndMerge = <T>(source: T, target: T): T => {
  const cp = deepCopy(target)
  merge(source, cp)
  return cp
}
